import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import CTA from "../components/cta";
import "./villas-for-sale-abu-dhabi.css";

const areas = [
  { icon: "fas fa-water", title: "Al Reem Island", text: "A very famous place for people who love modern living. It has many tall towers with amazing views of the sea and mangroves." },
  { icon: "fas fa-ticket-alt", title: "Yas Island", text: "The best choice if you like theme parks and big malls. A 2 bedroom apartment for rent in Abu Dhabi here gives you a fun life." },
  { icon: "fas fa-umbrella-beach", title: "The Corniche", text: "Perfect for families who enjoy being near the blue beach. It has long paths for walking and many green gardens for your children." },
  { icon: "fas fa-landmark", title: "Saadiyat Island", text: "A luxury area with beautiful beaches and many cultural museums. It is a premium place to live for those who want a quiet lifestyle." },
  { icon: "fas fa-road", title: "Al Raha Beach", text: "Offers a wonderful waterfront life with many shops and cafes. It is a great spot for professionals who want a modern and easy home." },
];

const features = [
  { icon: "fas fa-sun", title: "Large windows that let in a lot of bright and warm sunlight." },
  { icon: "fas fa-kitchen-set", title: "Modern kitchens that are fully ready for you to cook your meals." },
  { icon: "fas fa-box-archive", title: "Big built-in wardrobes to keep all your clothes and items organized." },
  { icon: "fas fa-fan", title: "Strong central air conditioning to keep the rooms very cool and fresh." },
  { icon: "fas fa-square-parking", title: "Private and safe parking spaces for your cars inside the building." },
  { icon: "fas fa-door-open", title: "Spacious balconies where you can sit and enjoy the fresh evening air." },
  { icon: "fas fa-elevator", title: "High-speed elevators that make reaching your floor very fast and easy." },
];

const amenities = [
  { icon: "fas fa-person-swimming", title: "Clean swimming pools and gyms are available for all of the residents." },
  { icon: "fas fa-child-reaching", title: "Safe play areas where your children can meet and play with friends." },
  { icon: "fas fa-bag-shopping", title: "Huge malls like Yas Mall are close by for all your shopping needs." },
  { icon: "fas fa-briefcase-medical", title: "Top hospitals and clinics are nearby to keep your family very healthy." },
  { icon: "fas fa-tree", title: "Beautiful community parks that are perfect for walking or having a picnic." },
  { icon: "fas fa-paw", title: "Pet-friendly buildings that welcome your furry friends into their new home." },
  { icon: "fas fa-shield-halved", title: "Security teams that work every hour to keep the whole building safe." },
];

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://nvrealestate.ae/" },
    { "@type": "ListItem", position: 2, name: "2 Bedroom Apartment for Rent in Abu Dhabi", item: "https://nvrealestate.ae/2-bedroom-apartment-for-rent-abu-dhabi" },
  ],
};

function TwoBedroomApartmentRent() {
  return (
    <>
      <Helmet>
        <title>2 Bedroom Apartment for Rent in Abu Dhabi – Spacious</title>
        <meta
          name="description"
          content="Searching for a 2-bedroom apartment for rent in Abu Dhabi? NV Real Estate can help you find the best apartment based on your needs. Let's talk now."
        />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <main
        className="villa-page"
        style={{ "--villa-section-image": `url(${process.env.PUBLIC_URL}/assets/images/abu.jpg)` }}
      >
        <section className="villa-hero" aria-labelledby="villa-page-title">
          <div
            className="villa-hero__image"
            aria-hidden="true"
            style={{ backgroundImage: `url("/images/seo/2-bedroom-apartment-rent/01.png")` }}
          />
          <div className="villa-hero__overlay" aria-hidden="true" />
          <div className="container villa-hero__inner">
            <div className="villa-hero__content">
              <span className="villa-eyebrow">Abu Dhabi rental specialists</span>
              <h1 id="villa-page-title">2 Bedroom Apartment for Rent in Abu Dhabi</h1>
              <div className="villa-hero__actions">
                <Link className="villa-button villa-button--primary" to="/property-search-result?offering_type=Rent&city=&developer=&developer_name=&project=&project_name=&property_type=Apartment&bedrooms=2&bathrooms=&minArea=&maxArea=&minPrice=&maxPrice=">
                  Explore apartments
                </Link>
                <a className="villa-button villa-button--ghost" href="tel:+971501577822">
                  Call +971 50 157 7822
                </a>
              </div>
            </div>
          </div>
        </section>

        <section className="villa-intro villa-section">
          <div className="container villa-intro__grid">
            <div className="villa-intro__image">
              <img
                src="/images/seo/2-bedroom-apartment-rent/02.png"
                alt="2 bedroom apartments for rent in Abu Dhabi"
              />
              <div className="villa-intro__note">
                <strong>Safest city living</strong>
                <span>Spacious homes for you and your family</span>
              </div>
            </div>
            <div className="villa-section-heading villa-intro__content">
              <span className="villa-kicker">Nova Vision Real Estate</span>
              <h2>Find Your Perfect Two Bedroom Apartment for Rent in Abu Dhabi with NV Real Estate</h2>
              <p>
                Finding a new home is a very exciting step for you and your family. Abu Dhabi is
                famous for being one of the safest cities in the world.
              </p>
              <p>
                Nova Vision Real Estate is here to help you find the best local homes. A two
                bedroom apartment for rent in Abu Dhabi offers the perfect space.
              </p>
            </div>
          </div>
        </section>

        <section className="villa-benefits villa-benefits--centered-cards villa-section" aria-labelledby="areas-heading">
          <div className="container">
            <div className="villa-section-heading villa-section-heading--center">
              <span className="villa-kicker">Where to live</span>
              <h2 id="areas-heading">Popular Areas for Your Apartment</h2>
            </div>
            <div className="villa-benefits__grid">
              {areas.map((area, index) => (
                <article className="villa-benefit-card" key={area.title}>
                  <span className="villa-benefit-card__number" aria-hidden="true">0{index + 1}</span>
                  <span className="villa-benefit-card__icon" aria-hidden="true"><i className={area.icon} /></span>
                  <h3>{area.title}</h3>
                  <p>{area.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="villa-benefits villa-benefits--centered-cards villa-section" aria-labelledby="features-heading">
          <div className="container">
            <div className="villa-section-heading villa-section-heading--center">
              <span className="villa-kicker">Apartment features</span>
              <h2 id="features-heading">High-Quality Features in Your New Apartment</h2>
            </div>
            <div className="villa-benefits__grid">
              {features.map((item, index) => (
                <article className="villa-benefit-card" key={item.title}>
                  <span className="villa-benefit-card__number" aria-hidden="true">0{index + 1}</span>
                  <span className="villa-benefit-card__icon" aria-hidden="true"><i className={item.icon} /></span>
                  <h3>{item.title}</h3>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="villa-benefits villa-benefits--centered-cards villa-section" aria-labelledby="amenities-heading">
          <div className="container">
            <div className="villa-section-heading villa-section-heading--center">
              <span className="villa-kicker">Community amenities</span>
              <h2 id="amenities-heading">Amazing Amenities for You and Your Family</h2>
            </div>
            <div className="villa-benefits__grid">
              {amenities.map((item, index) => (
                <article className="villa-benefit-card" key={item.title}>
                  <span className="villa-benefit-card__number" aria-hidden="true">0{index + 1}</span>
                  <span className="villa-benefit-card__icon" aria-hidden="true"><i className={item.icon} /></span>
                  <h3>{item.title}</h3>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="villa-benefits villa-section" aria-labelledby="steps-heading">
          <div className="container">
            <div className="villa-section-heading villa-section-heading--center">
              <span className="villa-kicker">The rental process</span>
              <h2 id="steps-heading">Vital Steps to Rent Your New Home</h2>
              <p>
                Finding a 2-bedroom apartment for rent in Abu Dhabi is easy with us. We know all the
                best buildings with the most amazing views for you.
              </p>
              <p>
                You must have your visa and your Emirates ID ready to sign the contract. NV Real
                Estate will help you collect all the right documents.
              </p>
              <p>
                Every rental home must be registered in the official Tawtheeq system today. This
                special system is very important because it protects your legal rights.
              </p>
              <p>
                Some islands now use the new ADGM system for all their rental contracts. Our expert
                team knows these rules well to give you the best advice.
              </p>
              <p>
                Most landlords will ask you to pay the rent with one or two cheques. We can help you
                talk to the landlord about the best payment plan.
              </p>
              <p>
                Before you move in, we will check the apartment for any small repairs. We want to
                make sure your new home is clean and ready for you.
              </p>
            </div>
          </div>
        </section>

        <section className="villa-advisor villa-section">
          <div className="container">
            <div className="villa-advisor__card villa-advisor__card--single">
              <div>
                <span className="villa-kicker">Why Nova Vision</span>
                <h2>Why Nova Vision Real Estate is Your Best Partner</h2>
                <p>
                  We are licensed experts who truly understand the Abu Dhabi property market. Our
                  professional team listens to exactly what you need for your new home and help you
                  find a two-bedroom apartment for rent in Abu Dhabi fast.
                </p>
                <p className="villa-advisor__closing">
                  Trust our experience to make your move into a new home very easy. Contact us today
                  to start your journey in this beautiful and safe city.
                </p>
                <a className="villa-button villa-button--light" href="https://wa.me/971501577822" target="_blank" rel="noreferrer">
                  Chat on WhatsApp
                </a>
                <p className="villa-advisor__closing">
                  Call us now at <a href="tel:+971501577822">+971-501577822</a>. You can also send
                  your requirements at <a href="mailto:info@nvrealestate.ae">info@nvrealestate.ae</a>.
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <CTA />

      <style>{`
        .villa-benefits--centered-cards .villa-benefits__grid {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 24px;
        }
        .villa-benefits--centered-cards .villa-benefit-card {
          flex: 1 1 260px;
          max-width: 300px;
        }
        @media (max-width: 700px) {
          .villa-benefits--centered-cards .villa-benefit-card {
            flex: 1 1 100%;
            max-width: 420px;
          }
        }
        .villa-advisor__card--single {
          display: block;
        }
      `}</style>
    </>
  );
}

export default TwoBedroomApartmentRent;